import { baseApi, RETRYABLE_WRITE_TIMEOUT_MS } from './baseApi';
import {
  Order,
  CreateOrderRequest,
  CreateOrderItemRequest,
  UpdateOrderRequest,
  AddOrderItemRequest,
  CancelOrderItemRequest,
  CancelOrderItemsRequest,
  OrderItemCancellation,
  CreateTakeOutOrderRequest,
  CreateDeliveryOrderRequest,
  CreateInternalConsumptionOrderRequest,
  TakeOutDeliveryOrder,
  PaymentMethodChange,
  OrderFilter,
  PagedResult,
} from '../types/orders';
import type { KitchenOrderDto } from '../types/kitchen';

export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<Order[], { activeOnly?: boolean }>({
      query: ({ activeOnly = false }) => `/orders?activeOnly=${activeOnly}`,
      providesTags: ['Order'],
    }),
    getOrdersPaged: builder.query<PagedResult<Order>, OrderFilter>({
      query: (filter) => {
        const params = new URLSearchParams();
        params.append('pageNumber', String(filter.pageNumber ?? 1));
        params.append('pageSize', String(filter.pageSize ?? 20));
        if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
        if (filter.status && filter.status !== 'All') params.append('status', filter.status);
        if (filter.paymentMethodId && filter.paymentMethodId !== 'All') {
          params.append('paymentMethodId', filter.paymentMethodId);
        }
        if (filter.fechaInicio) params.append('fechaInicio', filter.fechaInicio);
        if (filter.fechaFin) params.append('fechaFin', filter.fechaFin);
        return `/orders/paged?${params.toString()}`;
      },
      providesTags: ['Order'],
    }),
    getOrdersByTable: builder.query<Order[], string>({
      query: (tableId) => `/orders/table/${tableId}`,
      providesTags: ['Order'],
    }),
    getActiveOrderByTable: builder.query<Order, string>({
      query: (tableId) => `/orders/table/${tableId}/active`,
      providesTags: (_result, _error, tableId) => [{ type: 'Order', id: `table-${tableId}` }],
    }),
    getOrder: builder.query<Order, string>({
      query: (id) => `/orders/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),
    // Crear, comandear y cobrar llevan Id del cliente: el servidor reconoce el reintento,
    // así que se pueden reintentar solas cuando no hubo respuesta (ver RetryableWriteOptions).
    createOrder: builder.mutation<Order, CreateOrderRequest>({
      query: (newOrder) => ({
        url: '/orders',
        method: 'POST',
        body: newOrder,
        timeout: RETRYABLE_WRITE_TIMEOUT_MS,
      }),
      extraOptions: { retryWhenUnreachable: true },
      invalidatesTags: ['Order', 'DailyMenuCounts'],
    }),
    updateOrder: builder.mutation<Order, { id: string } & UpdateOrderRequest>({
      query: ({ id, ...patch }) => ({
        url: `/orders/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => ['Order', { type: 'Order', id }],
    }),
    updateOrderStatus: builder.mutation<void, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/orders/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Order'],
    }),
    addOrderItem: builder.mutation<Order, { orderId: string } & AddOrderItemRequest>({
      query: ({ orderId, ...item }) => ({
        url: `/orders/${orderId}/items`,
        method: 'POST',
        body: item,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'DailyMenuCounts',
      ],
    }),
    addMultipleOrderItems: builder.mutation<Order, {
      orderId: string;
      items: CreateOrderItemRequest[];
      /** Solo al sincronizar la caja offline: hora real en que se comandó. */
      occurredAt?: string;
    }>({
      query: ({ orderId, items, occurredAt }) => ({
        url: `/orders/${orderId}/items/bulk`,
        method: 'POST',
        body: { items, occurredAt },
        timeout: RETRYABLE_WRITE_TIMEOUT_MS,
      }),
      extraOptions: { retryWhenUnreachable: true },
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'DailyMenuCounts',
      ],
    }),
    removeOrderItem: builder.mutation<void, { orderId: string; itemId: string }>({
      query: ({ orderId, itemId }) => ({
        url: `/orders/${orderId}/items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'DailyMenuCounts',
      ],
    }),
    closeOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `/orders/${id}/close`,
        method: 'POST',
      }),
      // Closing an order may flip the table back to Available — invalidate
      // 'Table' so the tables grid in this client refreshes immediately.
      invalidatesTags: ['Order', 'Table'],
    }),
    cancelOrder: builder.mutation<void, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/orders/${id}/cancel`,
        method: 'POST',
        body: { reason },
      }),
      // Same rationale as closeOrder — cancelling can free the table.
      invalidatesTags: ['Order', 'DailyMenuCounts', 'Table'],
    }),
    // Etiqueta de comensal de un renglón (null = compartido); no mueve dinero
    setOrderItemSeat: builder.mutation<void, { orderId: string; itemId: string; seatNumber: number | null }>({
      query: ({ orderId, itemId, seatNumber }) => ({
        url: `/orders/${orderId}/items/${itemId}/seat`,
        method: 'PATCH',
        body: { seatNumber },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [{ type: 'Order', id: orderId }, 'Order'],
    }),
    // Corrige adultos/niños de una mesa abierta. Solo esa orden y su mesa, no
    // todas las listas de órdenes; 'Table' porque la tarjeta muestra los comensales.
    updateOrderGuests: builder.mutation<void, { orderId: string; tableId: string; adults: number; children: number }>({
      query: ({ orderId, adults, children }) => ({
        url: `/orders/${orderId}/guests`,
        method: 'PATCH',
        body: { adults, children },
      }),
      invalidatesTags: (_result, _error, { orderId, tableId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: `table-${tableId}` },
        'Table',
      ],
    }),
    calculateOrderTotal: builder.query<{ total: number }, string>({
      query: (id) => `/orders/${id}/total`,
    }),
    cancelOrderItem: builder.mutation<OrderItemCancellation, { orderId: string } & CancelOrderItemRequest>({
      query: ({ orderId, ...cancelData }) => ({
        url: `/orders/${orderId}/items/cancel`,
        method: 'POST',
        body: cancelData,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'DailyMenuCounts',
      ],
    }),
    cancelOrderItems: builder.mutation<OrderItemCancellation[], { orderId: string } & CancelOrderItemsRequest>({
      query: ({ orderId, ...cancelData }) => ({
        url: `/orders/${orderId}/items/cancel-multiple`,
        method: 'POST',
        body: cancelData,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'DailyMenuCounts',
      ],
    }),
    canCancelOrderItem: builder.query<{ canCancel: boolean; orderItemId: string }, string>({
      query: (orderItemId) => `/orders/items/${orderItemId}/can-cancel`,
    }),
    getOrderCancellations: builder.query<OrderItemCancellation[], string>({
      query: (orderId) => `/orders/${orderId}/cancellations`,
      providesTags: (_result, _error, orderId) => [{ type: 'Order', id: `${orderId}-cancellations` }],
    }),
    // Pre-check endpoints
    generatePreCheck: builder.mutation<{ preCheckId: string; billData: any }, { orderId: string; tableId: string }>({
      query: ({ orderId, tableId }) => ({
        url: `/orders/${orderId}/precheck`,
        method: 'POST',
        body: { tableId },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [{ type: 'Order', id: orderId }],
    }),
    getPreCheck: builder.query<any, string>({
      query: (preCheckId) => `/precheck/${preCheckId}`,
    }),
    markTableAsPreCheck: builder.mutation<void, { tableId: string; orderId: string }>({
      query: ({ tableId, orderId }) => ({
        url: `/tables/${tableId}/precheck`,
        method: 'POST',
        body: { orderId },
      }),
      invalidatesTags: ['Order'],
    }),
    // Payment endpoints
    processPayment: builder.mutation<{ receiptId: string; receiptData: any; isReplay?: boolean }, {
      orderId: string;
      tableId?: string; // Optional for TakeOut/Delivery orders
      // `id`: Id del pago generado en el cliente; con él un reintento no cobra dos veces.
      payments: Array<{ id?: string; methodId: string; amount: number; reference?: string }>;
      tip: number;
      discountAmount?: number;
      discountPercentage?: number;
      /** Solo al sincronizar la caja offline: hora real en que se cobró. */
      occurredAt?: string;
    }>({
      query: ({ orderId, tableId, payments, tip, discountAmount, discountPercentage, occurredAt }) => ({
        url: `/orders/${orderId}/payment`,
        method: 'POST',
        body: {
          tableId: tableId || null, // Send null for TakeOut/Delivery orders
          payments,
          tip,
          discountAmount: discountAmount || 0,
          discountPercentage: discountPercentage || 0,
          occurredAt,
        },
        timeout: RETRYABLE_WRITE_TIMEOUT_MS,
      }),
      // Solo es seguro porque cada pago lleva Id; sin él no se reintentaría.
      extraOptions: { retryWhenUnreachable: true },
      invalidatesTags: [
        'Order',
        // Invalidate cash register cache when payment is processed
        { type: 'CashRegister' as const, id: 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'LIST' },
        { type: 'CashMovement' as const, id: 'LIST' },
        // Payment may free the table (when it's the last Open order on it).
        // We invalidate the bulk 'Table' tag because the table-list queries
        // (`getTables`, `getTablesByArea`) provide the bare 'Table' tag, so
        // a per-id invalidation would not refetch them anyway. RTK Query
        // batches refetches per render frame, so the cost is small.
        'Table',
      ],
    }),
    generateReceipt: builder.query<any, string>({
      query: (receiptId) => `/receipts/${receiptId}`,
    }),
    // Discount endpoints
    applyDiscount: builder.mutation<any, {
      orderId: string;
      /** Renglón al que aplica; sin él, el descuento es a la cuenta completa. */
      orderItemId?: string;
      type: 'percentage' | 'amount';
      value: number; 
      reason: string; 
      notes?: string; 
      authorizedBy: string; 
    }>({
      query: ({ orderId, ...discountData }) => ({
        url: `/orders/${orderId}/discounts`,
        method: 'POST',
        body: discountData,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: `${orderId}-discounts` },
        { type: 'Order', id: `${orderId}-discount-summary` },
      ],
    }),
    getOrderDiscounts: builder.query<any[], string>({
      query: (orderId) => `/orders/${orderId}/discounts`,
      providesTags: (_result, _error, orderId) => [{ type: 'Order', id: `${orderId}-discounts` }],
    }),
    getDiscountSummary: builder.query<any, string>({
      query: (orderId) => `/orders/${orderId}/discount-summary`,
      providesTags: (_result, _error, orderId) => [{ type: 'Order', id: `${orderId}-discount-summary` }],
    }),
    removeDiscount: builder.mutation<void, string>({
      query: (discountId) => ({
        url: `/orders/discounts/${discountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Order'],
    }),
    canApplyDiscount: builder.query<{ canApplyDiscount: boolean }, string>({
      query: (orderId) => `/orders/${orderId}/can-apply-discount`,
    }),
    // TakeOut / Delivery endpoints. Llevan Id del cliente (orden y renglones), así que
    // se pueden reintentar solas igual que crear orden en mesa.
    createTakeOutOrder: builder.mutation<TakeOutDeliveryOrder, CreateTakeOutOrderRequest>({
      query: (orderData) => ({
        url: '/orders/takeout',
        method: 'POST',
        body: orderData,
        timeout: RETRYABLE_WRITE_TIMEOUT_MS,
      }),
      extraOptions: { retryWhenUnreachable: true },
      invalidatesTags: ['Order', 'DailyMenuCounts'],
    }),
    createDeliveryOrder: builder.mutation<TakeOutDeliveryOrder, CreateDeliveryOrderRequest>({
      query: (orderData) => ({
        url: '/orders/delivery',
        method: 'POST',
        body: orderData,
        timeout: RETRYABLE_WRITE_TIMEOUT_MS,
      }),
      extraOptions: { retryWhenUnreachable: true },
      invalidatesTags: ['Order', 'DailyMenuCounts'],
    }),
    // Consumo interno (comida de personal)
    createInternalConsumptionOrder: builder.mutation<TakeOutDeliveryOrder, CreateInternalConsumptionOrderRequest>({
      query: (orderData) => ({
        url: '/orders/internal-consumption',
        method: 'POST',
        body: orderData,
      }),
      invalidatesTags: ['Order', 'DailyMenuCounts'],
    }),
    closeInternalConsumptionOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `/orders/${id}/close-internal`,
        method: 'POST',
      }),
      // Closing deducts inventory — refresh stock AND the movements/Kardex view
      invalidatesTags: ['Order', 'Table', 'InventoryItem', 'InventoryMovement'],
    }),
    // Payment method change endpoints
    updatePaymentMethod: builder.mutation<void, {
      orderId: string;
      paymentId: string;
      newPaymentMethodId: string;
      reason: string;
      notes?: string;
    }>({
      query: ({ orderId, ...body }) => ({
        url: `/orders/${orderId}/payment`,
        method: 'PATCH',
        body: {
          paymentId: body.paymentId,
          newPaymentMethodId: body.newPaymentMethodId,
          reason: body.reason,
          notes: body.notes,
        },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        'Order',
        { type: 'Order', id: orderId },
        { type: 'Order', id: `${orderId}-changes` },
        // Invalidate all cash register related tags to refresh cash register view
        'CashRegister',
        { type: 'CashRegister' as const, id: 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'ALL_OPEN' },
        { type: 'CashRegister' as const, id: 'LIST' },
        { type: 'CashMovement' as const, id: 'LIST' },
      ],
    }),
    getPaymentMethodChanges: builder.query<PaymentMethodChange[], string>({
      query: (orderId) => `/orders/${orderId}/payment-changes`,
      providesTags: (_result, _error, orderId) => [
        { type: 'Order', id: orderId },
        { type: 'Order', id: `${orderId}-changes` },
      ],
    }),
    addDailyMenuSelections: builder.mutation<
      {
        order: Order;
        kitchenOrder: KitchenOrderDto | null;
        newKitchenOrderItemIds: string[];
        reactivated: boolean;
      },
      {
        orderId: string;
        itemId: string;
        newSelections: Array<{ courseId: string; optionId?: string }>;
      }
    >({
      query: ({ orderId, itemId, newSelections }) => ({
        url: `/orders/${orderId}/items/${itemId}/daily-menu-selections`,
        method: 'POST',
        body: { newSelections },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'Order', id: orderId },
        'Order',
        'DailyMenuCounts',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOrdersQuery,
  useGetOrdersPagedQuery,
  useGetOrdersByTableQuery,
  useGetActiveOrderByTableQuery,
  useGetOrderQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useUpdateOrderStatusMutation,
  useAddOrderItemMutation,
  useAddMultipleOrderItemsMutation,
  useRemoveOrderItemMutation,
  useCloseOrderMutation,
  useCancelOrderMutation,
  useCalculateOrderTotalQuery,
  useCancelOrderItemMutation,
  useSetOrderItemSeatMutation,
  useUpdateOrderGuestsMutation,
  useCancelOrderItemsMutation,
  useCanCancelOrderItemQuery,
  useGetOrderCancellationsQuery,
  // Pre-check hooks
  useGeneratePreCheckMutation,
  useGetPreCheckQuery,
  useMarkTableAsPreCheckMutation,
  // Payment hooks
  useProcessPaymentMutation,
  useGenerateReceiptQuery,
  // Discount hooks
  useApplyDiscountMutation,
  useGetOrderDiscountsQuery,
  useGetDiscountSummaryQuery,
  useRemoveDiscountMutation,
  useCanApplyDiscountQuery,
  // TakeOut / Delivery hooks
  useCreateTakeOutOrderMutation,
  useCreateDeliveryOrderMutation,
  // Consumo interno hooks
  useCreateInternalConsumptionOrderMutation,
  useCloseInternalConsumptionOrderMutation,
  // Payment method change hooks
  useUpdatePaymentMethodMutation,
  useGetPaymentMethodChangesQuery,
  // Daily-menu incremental send
  useAddDailyMenuSelectionsMutation,
} = ordersApi;